import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';
import { mapVoiceAgent } from './voiceAgents';
import type { VoiceAgentRow } from './voiceAgents';

// ── Agent Builder (Chat Builder) (/api/agent-builder) ────────────────

export type AgentBuilderCapturedField = {
  label: string;
  value: string;
  field_key: string;
};

/** Progress rows returned by the agent-builder chat API (`checklist` in JSON). */
export type AgentBuilderChecklistItem = {
  key: string;
  label: string;
  required: boolean;
  completed: boolean;
};

export type AgentBuilderChatResponse = {
  message: string;
  captured_fields: AgentBuilderCapturedField[];
  /** True when the backend is ready for `finalizeAgentFromBuilder` (maps `ready_to_create`). */
  is_complete: boolean;
  checklist?: AgentBuilderChecklistItem[];
  ready_to_create?: boolean;
};

/** Maps wire formats (`reply`, `extracted_fields`, `checklist`) to what the UI expects. */
function normalizeAgentBuilderChatResponse(raw: unknown): AgentBuilderChatResponse {
  const d = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};

  const message =
    (typeof d.reply === 'string' && d.reply) ||
    (typeof d.message === 'string' && d.message) ||
    '';

  const checklistRaw = d.checklist;
  const checklist = Array.isArray(checklistRaw)
    ? (checklistRaw as AgentBuilderChecklistItem[]).filter(
        (x): x is AgentBuilderChecklistItem =>
          typeof x?.key === 'string' && typeof x?.label === 'string'
      )
    : undefined;

  const labelByKey = new Map<string, string>();
  if (checklist) {
    for (const item of checklist) {
      labelByKey.set(item.key, item.label);
    }
  }

  let captured_fields: AgentBuilderCapturedField[] = [];

  if (Array.isArray(d.captured_fields)) {
    captured_fields = d.captured_fields as AgentBuilderCapturedField[];
  } else {
    const merged: Record<string, unknown> = {};
    const ext = d.extracted_fields;
    const flds = d.fields;
    if (ext && typeof ext === 'object' && !Array.isArray(ext)) Object.assign(merged, ext);
    if (flds && typeof flds === 'object' && !Array.isArray(flds)) Object.assign(merged, flds);

    for (const [key, val] of Object.entries(merged)) {
      if (val == null || val === '') continue;
      let valueStr: string;
      if (typeof val === 'string') valueStr = val;
      else if (typeof val === 'number' || typeof val === 'boolean') valueStr = String(val);
      else valueStr = JSON.stringify(val);
      captured_fields.push({
        field_key: key,
        label: labelByKey.get(key) ?? key,
        value: valueStr,
      });
    }
  }

  const ready =
    typeof d.ready_to_create === 'boolean'
      ? d.ready_to_create
      : typeof d.is_complete === 'boolean'
        ? d.is_complete
        : false;

  return {
    message,
    captured_fields,
    is_complete: ready,
    checklist,
    ready_to_create: ready || undefined,
  };
}

/** `POST /api/agent-builder/sessions` — returns a `session_id` for chat + create. */
export async function createAgentBuilderSession(
  opts: { campaignId?: string | null } = {},
  axiosInstance?: AxiosInstance
): Promise<string> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.agentBuilder}/sessions`;
  const body = opts.campaignId ? { campaign_id: opts.campaignId } : {};
  const response = await client.post<any>(url, body);
  const d = response.data;
  const sid = d?.session_id ?? d?.id ?? d?.uuid;
  if (!sid) throw new Error('Agent builder session did not return an id');
  return String(sid);
}

export async function chatWithAgentBuilder(
  input: { message: string; session_id: string; context?: any },
  axiosInstance?: AxiosInstance
): Promise<AgentBuilderChatResponse> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.agentBuilder}/chat`;
  const response = await client.post<unknown>(url, {
    session_id: input.session_id,
    message: input.message,
  });
  return normalizeAgentBuilderChatResponse(response.data);
}

export async function finalizeAgentFromBuilder(
  input: { session_id: string; captured_fields?: AgentBuilderCapturedField[] },
  axiosInstance?: AxiosInstance
): Promise<VoiceAgentRow> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.agentBuilder}/create`;
  const response = await client.post<any>(url, {
    session_id: input.session_id,
    captured_fields: input.captured_fields,
  });
  return mapVoiceAgent(response.data);
}

// ── Agent Builder extras ──────────────────────────────────────────────

export type AgentBuilderSession = { session_id: string; created_at?: string; campaign_id?: string | null };

/** `GET /api/agent-builder/sessions` — list all builder sessions. */
export async function fetchAgentBuilderSessions(axiosInstance?: AxiosInstance): Promise<AgentBuilderSession[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.agentBuilder}/sessions`;
  const response = await client.get<any>(url);
  const list = Array.isArray(response.data) ? response.data : response.data?.sessions ?? [];
  return list.map((s: any) => ({ session_id: String(s.session_id ?? s.id ?? ''), created_at: s.created_at, campaign_id: s.campaign_id ?? null }));
}

/** `GET /api/agent-builder/sessions/{session_id}` — get session with chat history. */
export async function fetchAgentBuilderSession(sessionId: string, axiosInstance?: AxiosInstance): Promise<{ session: AgentBuilderSession; messages: { role: string; content: string }[] } | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.agentBuilder}/sessions/${sessionId}`;
  const response = await client.get<any>(url);
  if (!response.data) return null;
  const d = response.data;
  return {
    session: { session_id: String(d.session_id ?? d.id ?? sessionId), created_at: d.created_at, campaign_id: d.campaign_id ?? null },
    messages: Array.isArray(d.messages) ? d.messages : [],
  };
}

/** `DELETE /api/agent-builder/sessions/{session_id}` — delete builder session. */
export async function deleteAgentBuilderSession(sessionId: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.delete(`${apiConfig.voiceEndpoints.agentBuilder}/sessions/${sessionId}`);
  return response.status === 200 || response.status === 204;
}
